import { useEffect, useReducer, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { emit } from '../../platform/analytics/analytics'
import type { ENUM_VALUES } from '../../platform/analytics/vocabulary'
import { useAnnouncement } from '../../platform/a11y/useAnnouncement'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Button } from '../../platform/primitives/Button'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { Select } from '../../platform/primitives/Select'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { signedCents } from './accountMoney'
import { accountTypeLabel, groupOf } from './accountGroups'
import { candidatesFor, type Relink, type RelinkAccount } from './relinkWire'
import { alreadyMatchedLine, fullyReconnectedLine, reconnectedMessage, UNKNOWN_BANK } from './relinkOutcome'
import { useAccounts } from './useAccounts'
import { useProviderItems } from './useAggregator'
import { RelinkResolveError, useRelinkState, useResolveRelink, type Answer } from './useRelink'
import './accounts.css'
import './relinkMatch.css'
import '../../platform/notes.css'

/** One select value per answer: '' (not answered), a new account's id, or one of these two. */
const KEEP = 'keep'
const LATER = 'later'

type Choice = Record<string, string>
type Action = { type: 'choose'; oldId: string; value: string } | { type: 'clear' }

function choices(state: Choice, action: Action): Choice {
  switch (action.type) {
    case 'choose':
      return { ...state, [action.oldId]: action.value }
    case 'clear':
      return {}
    default:
      action satisfies never
      return state
  }
}

const DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })

/** `YYYY-MM-DD` → "Sep 15", read as the calendar day it names. A bare-string `Date`
 *  is UTC midnight and, formatted in a western timezone, is the day before. */
function dayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (y === undefined || m === undefined || d === undefined) return iso
  return DAY.format(new Date(Date.UTC(y, m - 1, d)))
}

const accountLabel = (a: RelinkAccount) => (a.mask ? `${a.name} ••${a.mask}` : a.name)

/** The answers the user has given, read against what is STILL unmatched — a row the
 *  server has since paired on its own no longer counts. */
function answersOf(relink: Relink, chosen: Choice): Answer[] {
  const out: Answer[] = []
  for (const old of relink.unmatchedOld) {
    const v = chosen[old.id]
    if (v === undefined || v === '' || v === LATER) continue
    if (v === KEEP) out.push({ kind: 'keep', oldId: old.id })
    else if (relink.unmatchedNew.some((n) => n.id === v)) out.push({ kind: 'pair', oldId: old.id, newId: v })
  }
  return out
}

/** A failed save as analytics properties: an enum and a boolean, never the error's own text. */
function failureProps(thrown: unknown): {
  relink_error_kind: (typeof ENUM_VALUES.relink_error_kind)[number]
  partial_applied: boolean
} {
  if (!(thrown instanceof RelinkResolveError)) return { relink_error_kind: 'unexpected', partial_applied: false }
  return {
    relink_error_kind: thrown.unconfirmed ? 'unconfirmed' : thrown.error.kind,
    partial_applied: thrown.partial,
  }
}

function resolveErrorMessage(thrown: unknown): string {
  const kind = thrown instanceof RelinkResolveError ? thrown.error.kind : null
  // An earlier batch was saved and cannot be undone: never say nothing changed.
  if (thrown instanceof RelinkResolveError && thrown.partial) {
    return "Some matches were saved and the rest weren't. Check the rows, then save again."
  }
  // No answer came back: the save may or may not have landed.
  if (thrown instanceof RelinkResolveError && thrown.unconfirmed) {
    return "We couldn't confirm the save. Check the rows before saving again."
  }
  switch (kind) {
    case 'currency_mismatch':
      return "Those accounts are in different currencies, so they can't be matched. Nothing was changed."
    case 'account_changed':
    case 'already_paired':
      return 'These accounts changed while you were choosing. Check the rows, then save again. Nothing was changed.'
    case 'too_many':
      return 'That is too many answers at once. Save fewer, then the rest. Nothing was changed.'
    case 'not_found':
      return 'This reconnect is no longer active. Nothing was changed.'
    default:
      return "The matches couldn't be saved. Nothing was changed."
  }
}

/**
 * `/connections/reconnect/:itemId` — the user-confirmed match step for the
 * accounts a reconnect could not pair on its own (owner decision Q2).
 *
 * **Nothing is pre-selected.** A merge cannot be undone, so every answer is the
 * user's; a server suggestion is only a hint line under the row.
 */
export function RelinkMatch() {
  const { itemId = '' } = useParams()
  const state = useRelinkState(itemId)
  const items = useProviderItems()
  // `null` while the name is unknown; the copy helpers choose the fallback and its case.
  const bank =
    (items.status === 'success' || items.status === 'stale'
      ? items.data.find((i) => i.id === itemId)?.institutionName
      : null) ?? null
  return (
    <>
      <header className="connections__header">
        <div className="connections__titles">
          <h2 className="connections__title">Finish reconnecting {bank ?? UNKNOWN_BANK}</h2>
        </div>
      </header>
      <Async
        state={state}
        surface="relink-state"
        skeleton={
          <>
            <Skeleton height={72} />
            <Skeleton height={72} />
          </>
        }
        empty={{ title: 'Nothing to match' }}
      >
        {(relink) => <MatchBody itemId={itemId} bank={bank} relink={relink} />}
      </Async>
    </>
  )
}

function MatchBody({ itemId, bank, relink }: { itemId: string; bank: string | null; relink: Relink }) {
  const navigate = useNavigate()
  const toast = useToast()
  const bp = useBreakpoint()
  const phone = bp === 'phone'
  const { resolve } = useResolveRelink(itemId)
  // A matched account keeps its OLD id, so the accounts list names it.
  const accounts = useAccounts()
  const accountNames = new Map(
    accounts.status === 'success' || accounts.status === 'stale'
      ? accounts.data.map((a) => [a.id, a.name] as const)
      : [],
  )
  const [chosen, dispatch] = useReducer(choices, {})
  const [confirming, setConfirming] = useState(false)
  const [announcement, announce] = useAnnouncement()

  const answers = answersOf(relink, chosen)
  const pairs = answers.filter((a) => a.kind === 'pair')
  const keeps = answers.filter((a) => a.kind === 'keep')
  const usedNew = new Set(pairs.map((a) => a.newId))
  const usedByRow = (oldId: string) => {
    const mine = answers.find((a) => a.kind === 'pair' && a.oldId === oldId)
    return mine?.kind === 'pair' ? mine.newId : null
  }
  const nameOf = (id: string) => {
    const a = [...relink.unmatchedOld, ...relink.unmatchedNew].find((x) => x.id === id)
    return a ? accountLabel(a) : 'an account'
  }
  // The confirm outlives the dialog: the user may have left by the time it settles.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // The step was shown: once per mount, a count and a status, never which accounts.
  const opened = useRef(false)
  useEffect(() => {
    if (opened.current) return
    opened.current = true
    emit('relink_review_opened', { account_count: relink.unmatchedOld.length, relink_status: relink.status })
  }, [relink.unmatchedOld.length, relink.status])

  if (relink.status === 'completed') {
    return (
      <Panel className="accounts-stack">
        <p className="note">{fullyReconnectedLine(bank)}</p>
        <InlineLink to="/connections">Back to connections</InlineLink>
      </Panel>
    )
  }

  const unusedNew = relink.unmatchedNew.filter((n) => !usedNew.has(n.id))

  const confirm = async () => {
    // What the user answered, by count only. `undecided` is the rows with no answer.
    const counts = {
      paired_count: pairs.length,
      kept_separate_count: keeps.length,
      undecided_count: relink.unmatchedOld.length - answers.length,
    }
    let out: Awaited<ReturnType<typeof resolve>>
    try {
      out = await resolve(answers)
    } catch (thrown) {
      // Once per resolve call. A failure shown as a different failure is the worst a
      // dev-time registration throw can do here; the success path below emits LAST.
      emit('relink_resolved', { ...counts, ...failureProps(thrown) })
      throw thrown
    }
    // The success emit comes AFTER the state, toast and navigation (#399): the save
    // landed, and a registration throw must not rewrite it as "Nothing was changed".
    // One emit for both branches, so it fires whether or not the step is still mounted.
    const succeeded = () =>
      emit('relink_resolved', { ...counts, relink_status: out.relink.status, partial_applied: false })
    if (!mounted.current) {
      if (out.relink.status === 'completed') toast.show({ kind: 'success', message: reconnectedMessage(bank) })
      succeeded()
      return
    }
    setConfirming(false)
    dispatch({ type: 'clear' })
    if (out.dropped.length > 0) {
      const n = out.dropped.length
      announce(`${n} ${n === 1 ? 'account was' : 'accounts were'} matched automatically meanwhile`)
    }
    if (out.relink.status === 'completed') {
      toast.show({ kind: 'success', message: reconnectedMessage(bank) })
      navigate('/connections')
    } else {
      // A partial save is allowed by design; say what is left, to everyone.
      const left = out.relink.unmatchedOld.length
      toast.show({
        kind: 'success',
        message:
          left > 0 ? `Saved. ${left} ${left === 1 ? 'account still needs' : 'accounts still need'} matching.` : 'Saved.',
      })
    }
    succeeded()
  }

  return (
    <div className="relink-match">
      <p className="connections__lede">
        {relink.paired.length} matched automatically. Tell us what happened to the rest.
      </p>
      {relink.status === 'pending' && (
        <p className="note">Still syncing — some accounts may match on their own.</p>
      )}
      {/* A live region must be in the DOM BEFORE its text arrives, so it always renders. Empty,
          it is visually hidden (`.sr-only`, out of flow: it adds no height and no flex gap);
          with an announcement it is the visible note. */}
      <p role="status" className={announcement === '' ? 'sr-only' : 'note'}>
        {announcement}
      </p>

      {relink.unmatchedOld.length === 0 ? (
        <Panel className="accounts-stack">
          <p className="note">Nothing is left to match.</p>
          <InlineLink to="/connections">Back to connections</InlineLink>
        </Panel>
      ) : (
        <ul className="relink-match__list" aria-label="Accounts to match">
          {relink.unmatchedOld.map((old) => {
            const suggestion = relink.suggestions
              .filter((s) => s.oldId === old.id)
              .sort((a, b) => b.overlap - a.overlap)[0]
            const suggested = suggestion ? relink.unmatchedNew.find((n) => n.id === suggestion.newId) : undefined
            const mine = usedByRow(old.id)
            const label = `${accountLabel(old)} is now`
            return (
              <li key={old.id}>
                <Panel className={['relink-match__row', phone ? 'relink-match__row--stack' : ''].filter(Boolean).join(' ')}>
                  <div className="relink-match__account">
                    <span className="relink-match__name">{accountLabel(old)}</span>
                    <span className="relink-match__meta">
                      {accountTypeLabel(old.type)}
                      {old.balanceCents !== null && (
                        <>
                          {' · '}
                          <Money
                            cents={signedCents(old.balanceCents, groupOf(old.type))}
                            currency={old.currency}
                            kind="stock"
                          />
                        </>
                      )}
                      {old.lastTransactionDate !== null && ` · Last transaction ${dayLabel(old.lastTransactionDate)}`}
                    </span>
                  </div>
                  <div className="relink-match__answer">
                    <Select
                      label={label}
                      value={chosen[old.id] ?? ''}
                      onChange={(e) => dispatch({ type: 'choose', oldId: old.id, value: e.target.value })}
                    >
                      <option value="">Choose…</option>
                      {candidatesFor(relink, old.id).map(({ account, overlap }) => (
                        <option
                          key={account.id}
                          value={account.id}
                          disabled={usedNew.has(account.id) && mine !== account.id}
                        >
                          {accountLabel(account)}
                          {overlap !== null && ` · ${overlap} matching transactions`}
                        </option>
                      ))}
                      <option value={KEEP}>Closed — no longer at this bank</option>
                      <option value={LATER}>Decide later</option>
                    </Select>
                    {suggested && (
                      <p className="relink-match__hint">Suggested: {accountLabel(suggested)}</p>
                    )}
                  </div>
                </Panel>
              </li>
            )
          })}
        </ul>
      )}

      {relink.paired.length > 0 && (
        <Panel className="accounts-stack">
          <h3 className="accounts-total__label">Already matched</h3>
          <ul className="relink-match__list">
            {relink.paired.map((p) => (
              <li key={`${p.oldId}-${p.newId}`} className="relink-match__meta">
                {alreadyMatchedLine(accountNames.get(p.oldId), accountNames.get(p.newId), p.oldId === p.newId)}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {unusedNew.length > 0 && relink.unmatchedOld.length > 0 && (
        <p className="note">
          Also new: {unusedNew.map(accountLabel).join(', ')}. These stay as separate accounts.
        </p>
      )}

      {relink.unmatchedOld.length > 0 && (
        <div className="relink-match__actions">
          <Button
            variant="primary"
            size="page"
            disabled={answers.length === 0}
            aria-describedby={answers.length === 0 ? 'relink-save-reason' : undefined}
            onClick={() => setConfirming(true)}
          >
            Save matches
          </Button>
          {answers.length === 0 && (
            <span id="relink-save-reason" className="note">
              Choose an answer for at least one account.
            </span>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirming}
        mode="relink_resolve"
        title="Save these matches?"
        body={
          <>
            Merging can't be undone. Edits you made on the new copies of transactions you already had are not kept.
            {pairs.map((a) =>
              a.kind === 'pair' ? (
                <span key={a.oldId} className="relink-match__line">
                  Before: {nameOf(a.oldId)} ← Reconnected: {nameOf(a.newId)}
                </span>
              ) : null,
            )}
            {keeps.map((a) => (
              <span key={a.oldId} className="relink-match__line">
                {nameOf(a.oldId)} marked closed
              </span>
            ))}
          </>
        }
        confirmLabel="Save matches"
        pendingLabel="Saving…"
        errorFor={resolveErrorMessage}
        onCancel={() => setConfirming(false)}
        onConfirm={confirm}
      />
    </div>
  )
}
